import React, { useEffect, useMemo, useState } from 'react';
import {
  FiTrash2, FiEdit2, FiSun, FiMoon, FiGrid, FiPlusCircle, FiMinusCircle, FiDownload,
} from 'react-icons/fi';
import * as customerService from '../services/customerService';
import { useTheme } from '../context/ThemeContext';
import { useLocale } from '../context/LocaleContext';
import { useAuth } from '../context/AuthContext';
import { useModule } from '../context/ModuleContext';
import * as goldService from '../services/goldService';
import { exportCustomerGoldPDF } from '../services/exportService';

/* ---------------- Helpers ---------------- */

function formatGrams(n) {
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(parseFloat(n) || 0);
}

function formatPurity(p) {
  return (parseFloat(p) || 0).toFixed(2);
}

function formatDate(ts) {
  try {
    if (ts === null || ts === undefined || ts === '') return '';
    let d;
    if (typeof ts === 'number' || /^\d+$/.test(String(ts))) {
      const num = Number(ts);
      d = new Date(num < 1e12 ? num * 1000 : num);
    } else {
      d = new Date(ts);
    }
    if (isNaN(d.getTime())) return '' + ts;
    return d.toLocaleString();
  } catch {
    return '' + ts;
  }
}

/* ---------------- Component ---------------- */

export default function GoldDashboard() {
  const { user, logout } = useAuth();
  const { t, tPt, locale, setLocale } = useLocale();
  const { theme, toggleTheme } = useTheme();
  const { goToPicker } = useModule();

  // Customers
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  // Gold data
  const [lots, setLots] = useState([]);
  const [txs, setTxs] = useState([]);
  const [txSearchQuery, setTxSearchQuery] = useState('');
  const [exporting, setExporting] = useState(false);

  // Form state
  const [txType, setTxType] = useState('deposit'); // deposit | withdraw
  const [depositPurity, setDepositPurity] = useState('');     // for deposit (free input)
  const [withdrawLotId, setWithdrawLotId] = useState('');     // for withdraw (picker)
  const [unit, setUnit] = useState('g');                       // g | kg
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [txDate, setTxDate] = useState(() => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });

  // Client modal
  const [showClientModal, setShowClientModal] = useState(false);
  const [clientModalMode, setClientModalMode] = useState('create'); // 'create' | 'edit'
  const [editingUserId, setEditingUserId] = useState(null);
  const [newName, setNewName] = useState('');
  const [newInfo, setNewInfo] = useState('');
  const [newPhone, setNewPhone] = useState('');

  // Edit transaction modal
  const [showEditTx, setShowEditTx] = useState(false);
  const [editingTx, setEditingTx] = useState(null);
  const [editTxAmount, setEditTxAmount] = useState('');
  const [editTxDescription, setEditTxDescription] = useState('');
  const [editTxSubmitting, setEditTxSubmitting] = useState(false);

  // All-clients totals modal
  const [showTotals, setShowTotals] = useState(false);
  const [globalTotals, setGlobalTotals] = useState({ total_grams: 0, by_purity: [] });

  /* ---------- Load customers ---------- */
  useEffect(() => {
    const load = async () => {
      const result = await customerService.listCustomers();
      if (result.success && result.data) {
        setUsers(result.data);
        if (result.data.length && !selectedId) setSelectedId(result.data[0].id);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handle = setTimeout(async () => {
      if (query.trim()) {
        const r = await customerService.searchCustomers(query);
        if (r.success && r.data) {
          setUsers(r.data);
          if (r.data.length && (!selectedId || !r.data.find((c) => c.id === selectedId))) {
            setSelectedId(r.data[0].id);
          }
        }
      } else {
        const r = await customerService.listCustomers();
        if (r.success && r.data) setUsers(r.data);
      }
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const selectedUser = useMemo(
    () => (selectedId ? users.find((u) => u.id === selectedId) : null),
    [selectedId, users]
  );

  /* ---------- Load lots + transactions when client changes ---------- */
  const refreshGoldData = async () => {
    if (!selectedId) {
      setLots([]); setTxs([]); return;
    }
    const lotsRes = await goldService.getCustomerGoldLots(selectedId);
    setLots(lotsRes.success ? lotsRes.data : []);
    const txRes = await goldService.getCustomerGoldTransactions(selectedId);
    setTxs(txRes.success ? txRes.data : []);
  };

  useEffect(() => {
    refreshGoldData();
    // reset form when switching clients
    setWithdrawLotId('');
    setAmount('');
    setDescription('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  /* ---------- Transaction search ---------- */
  useEffect(() => {
    if (!selectedId) return;
    const handle = setTimeout(async () => {
      if (txSearchQuery.trim()) {
        const r = await goldService.searchCustomerGoldTransactions(selectedId, txSearchQuery);
        if (r.success) setTxs(r.data);
      } else {
        const r = await goldService.getCustomerGoldTransactions(selectedId);
        if (r.success) setTxs(r.data);
      }
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txSearchQuery, selectedId]);

  /* ---------- Derived ---------- */
  const totalGrams = useMemo(
    () => lots.reduce((s, l) => s + parseFloat(l.amount_grams || 0), 0),
    [lots]
  );

  /* ---------- Submit ---------- */
  const handleExportPDF = async () => {
    if (!selectedId) return;
    setExporting(true);
    try {
      const result = await exportCustomerGoldPDF(selectedId, tPt);
      if (!result.success) {
        alert(result.message || 'Failed to export PDF');
      }
    } catch (error) {
      console.error('Export gold PDF error:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedId) return;

    const rawAmount = parseFloat(amount);
    if (!Number.isFinite(rawAmount) || rawAmount <= 0) {
      alert(t('invalid_amount'));
      return;
    }
    const grams = unit === 'kg' ? rawAmount * 1000 : rawAmount;
    const createdAt = txDate ? Math.floor(new Date(txDate).getTime() / 1000) : null;

    setSubmitting(true);
    try {
      let result;
      if (txType === 'deposit') {
        const p = parseFloat(depositPurity);
        if (!Number.isFinite(p) || p < 0 || p > 100) {
          alert(t('invalid_purity'));
          setSubmitting(false);
          return;
        }
        result = await goldService.depositGold(selectedId, p, grams, description, createdAt);
      } else {
        if (!withdrawLotId) {
          alert(t('select_lot_first'));
          setSubmitting(false);
          return;
        }
        // Client-side guard against negative balances
        const lot = lots.find((l) => String(l.id) === String(withdrawLotId));
        if (lot && grams > parseFloat(lot.amount_grams)) {
          alert(t('insufficient_gold_in_lot'));
          setSubmitting(false);
          return;
        }
        result = await goldService.withdrawGold(selectedId, withdrawLotId, grams, description, createdAt);
      }

      if (!result.success) {
        alert(result.message || 'Failed');
      } else {
        setAmount('');
        setDescription('');
        await refreshGoldData();
      }
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------- Client CRUD ---------- */
  const openAddClient = () => {
    setClientModalMode('create');
    setEditingUserId(null);
    setNewName('');
    setNewInfo('');
    setNewPhone('');
    setShowClientModal(true);
  };

  const closeClientModal = () => setShowClientModal(false);

  const openEditClientFor = (u) => {
    if (!u) return;
    setClientModalMode('edit');
    setEditingUserId(u.id);
    setNewName(u.name || '');
    setNewInfo(u.info || u.email || '');
    setNewPhone(u.phone || '');
    setShowClientModal(true);
  };

  const handleSaveClient = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    if (clientModalMode === 'edit' && editingUserId) {
      const result = await customerService.updateCustomer(editingUserId, { name, info: newInfo, phone: newPhone });
      if (result.success) {
        const r2 = await customerService.listCustomers();
        if (r2.success) setUsers(r2.data);
      }
    } else {
      const result = await customerService.addCustomer({ name, info: newInfo, phone: newPhone });
      if (result.success && result.data) {
        setSelectedId(result.data.id);
        const r2 = await customerService.listCustomers();
        if (r2.success) setUsers(r2.data);
      }
    }
    setShowClientModal(false);
  };

  const handleDeleteClientById = async (id) => {
    if (!id) return;
    if (!window.confirm(t('delete_client_confirm'))) return;
    const result = await customerService.deleteCustomer(id);
    if (result.success) {
      if (selectedId === id) setSelectedId(null);
      const r2 = await customerService.listCustomers();
      if (r2.success) setUsers(r2.data);
    }
  };

  /* ---------- Edit tx ---------- */
  const openEditTx = (tx) => {
    setEditingTx(tx);
    setEditTxAmount(String(Math.abs(parseFloat(tx.amount_grams))));
    setEditTxDescription(tx.description || '');
    setShowEditTx(true);
  };

  const closeEditTx = () => {
    setShowEditTx(false);
    setEditingTx(null);
  };

  const handleSaveEditTx = async (e) => {
    e.preventDefault();
    if (!editingTx) return;
    const raw = parseFloat(editTxAmount);
    if (!Number.isFinite(raw) || raw <= 0) {
      alert(t('invalid_amount'));
      return;
    }
    setEditTxSubmitting(true);
    try {
      const result = await goldService.editGoldTransaction(editingTx.id, {
        amount_grams: raw,
        description: editTxDescription,
      });
      if (!result.success) {
        alert(result.message || 'Failed');
      } else {
        closeEditTx();
        await refreshGoldData();
      }
    } finally {
      setEditTxSubmitting(false);
    }
  };

  /* ---------- Delete tx ---------- */
  const handleDeleteTx = async (id) => {
    if (!id) return;
    if (!window.confirm(t('delete_tx_confirm'))) return;
    const r = await goldService.deleteGoldTransaction(id);
    if (!r.success) {
      alert(r.message || 'Failed');
      return;
    }
    await refreshGoldData();
  };

  /* ---------- Totals modal ---------- */
  const openTotals = async () => {
    setShowTotals(true);
    const r = await goldService.getAllClientsGoldBalance();
    if (r.success && r.data) setGlobalTotals(r.data);
  };
  const closeTotals = () => setShowTotals(false);

  /* ---------- Render ---------- */
  return (
    <div className="layout">
      {/* Header */}
      <header className="app-header">
        <div className="brand">
          <img src={process.env.PUBLIC_URL + '/assets/logo.png'} alt="Logo" />
          <h1 className="app-title">{t('brand_title')} — {t('module_gold')}</h1>
        </div>
        <div className="app-actions">
          <button className="btn btn-ghost btn-icon" onClick={goToPicker} title={t('back_to_modules')} aria-label={t('back_to_modules')}>
            <FiGrid />
          </button>
          <button className="btn btn-ghost" onClick={openTotals} title={t('all_clients_gold_total')}>
            {t('all_clients_gold_total')}
          </button>
          <button className="btn btn-ghost btn-icon" onClick={toggleTheme} title="Theme" aria-label="Toggle theme">
            {theme === 'dark' ? <FiSun /> : <FiMoon />}
          </button>
          <select className="lang-select" value={locale} onChange={(e) => setLocale(e.target.value)} aria-label="Language">
            <option value="en">English</option>
            <option value="pt-BR">Português (BR)</option>
          </select>
          <span className="muted">{t('signed_in_as')} {user?.username}</span>
          <button className="btn btn-ghost" onClick={logout}>{t('logout')}</button>
        </div>
      </header>

      <main className="content">
        {/* Customer list */}
        <section className="panel users">
          <div className="panel-header">
            <div className="panel-title">
              <h2>{t('customers')}</h2>
              <button
                type="button"
                className="btn btn-primary btn-icon"
                onClick={openAddClient}
                title={t('add_client')}
                aria-label={t('add_client')}
              >
                +
              </button>
            </div>
            <input
              className="search-input"
              type="text"
              placeholder={t('search_placeholder')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <ul className="list">
            {users.map((u) => (
              <li
                key={u.id}
                className={'user-item' + (u.id === selectedId ? ' active' : '')}
                onClick={() => setSelectedId(u.id)}
              >
                <div className="user-meta">
                  <div className="user-name">{u.name}</div>
                  {(u.info || u.phone) && <div className="user-sub muted">{u.info || u.phone}</div>}
                </div>
                <div className="user-actions-inline" onClick={(e) => e.stopPropagation()}>
                  <button type="button" className="btn btn-ghost btn-icon" onClick={() => openEditClientFor(u)} title={t('edit')} aria-label={t('edit')}>
                    <FiEdit2 />
                  </button>
                  <button type="button" className="btn btn-ghost btn-icon" onClick={() => handleDeleteClientById(u.id)} title={t('delete')} aria-label={t('delete')}>
                    <FiTrash2 />
                  </button>
                </div>
              </li>
            ))}
            {users.length === 0 && <li className="empty muted">{t('no_customers')}</li>}
          </ul>
        </section>

        {/* Workspace */}
        <section className="panel details">
          {selectedUser ? (
            <>
              {/* Top row: holdings + form */}
              <div className="two-col">
                {/* Holdings */}
                <div style={{
                  background: 'var(--card-bg)',
                  border: '1px solid var(--border)',
                  borderRadius: '14px',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    <h3 style={{ margin: 0, lineHeight: 1.2 }}>{t('gold_holdings')}</h3>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={handleExportPDF}
                      disabled={exporting}
                      title={t('export_pdf')}
                    >
                      <FiDownload style={{ marginRight: '6px' }} />
                      {exporting ? t('exporting') : t('export_pdf')}
                    </button>
                  </div>
                  <div className="muted small">
                    {selectedUser.name} • {t('total')}: <strong>{formatGrams(totalGrams)} g</strong>
                  </div>
                  {lots.length === 0 ? (
                    <div className="empty muted">{t('no_gold_lots')}</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {lots.map((l) => (
                        <div
                          key={l.id}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '10px 14px',
                            border: '1px solid var(--border)',
                            borderRadius: '10px',
                            background: 'var(--panel-2)',
                          }}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <span style={{ fontWeight: 600 }}>{formatPurity(l.purity)}%</span>
                            <span className="muted small">{t('lot')} #{l.id}</span>
                          </div>
                          <span style={{ fontSize: '16px', fontWeight: 700 }}>
                            {formatGrams(l.amount_grams)} g
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Make transaction */}
                <div className="card">
                  <h3>{t('make_gold_transaction')}</h3>
                  <form onSubmit={handleSubmit} className="form-grid">
                    {/* Type segmented */}
                    <label className="wide">
                      <span>{t('type')}</span>
                      <div className="segmented" role="tablist" aria-label="Transaction type">
                        <button
                          type="button"
                          className={'segmented-item' + (txType === 'deposit' ? ' active' : '')}
                          onClick={() => setTxType('deposit')}
                          role="tab"
                          aria-selected={txType === 'deposit'}
                        >
                          <FiPlusCircle style={{ marginRight: '6px' }} />
                          {t('deposit')}
                        </button>
                        <button
                          type="button"
                          className={'segmented-item' + (txType === 'withdraw' ? ' active' : '')}
                          onClick={() => setTxType('withdraw')}
                          role="tab"
                          aria-selected={txType === 'withdraw'}
                        >
                          <FiMinusCircle style={{ marginRight: '6px' }} />
                          {t('withdraw')}
                        </button>
                      </div>
                    </label>

                    {/* For deposit: free purity input. For withdraw: lot picker */}
                    {txType === 'deposit' ? (
                      <label className="wide">
                        <span>{t('purity')} (%)</span>
                        <div className="input-group">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            placeholder="e.g., 75.68"
                            value={depositPurity}
                            onChange={(e) => setDepositPurity(e.target.value)}
                            required
                          />
                        </div>
                        <span className="muted small">{t('purity_hint_deposit')}</span>
                      </label>
                    ) : (
                      <label className="wide">
                        <span>{t('withdraw_from_lot')}</span>
                        <div className="input-group">
                          <select
                            value={withdrawLotId}
                            onChange={(e) => setWithdrawLotId(e.target.value)}
                            required
                            disabled={lots.length === 0}
                          >
                            <option value="">
                              {lots.length === 0 ? t('no_gold_lots') : t('select_lot_placeholder')}
                            </option>
                            {lots.map((l) => (
                              <option key={l.id} value={l.id}>
                                {formatPurity(l.purity)}% — {formatGrams(l.amount_grams)} g
                              </option>
                            ))}
                          </select>
                        </div>
                        <span className="muted small">{t('purity_hint_withdraw')}</span>
                      </label>
                    )}

                    {/* Amount with unit selector */}
                    <label className="wide">
                      <span>{t('amount')}</span>
                      <div className="input-group amount">
                        <select
                          className="currency-select"
                          value={unit}
                          onChange={(e) => setUnit(e.target.value)}
                          aria-label={t('unit')}
                        >
                          <option value="g">g</option>
                          <option value="kg">kg</option>
                        </select>
                        <input
                          type="number"
                          step={unit === 'kg' ? '0.0001' : '0.001'}
                          min="0"
                          placeholder="0.000"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          required
                        />
                      </div>
                    </label>

                    {/* Description */}
                    <label className="wide">
                      <span>{t('description')}</span>
                      <div className="input-group">
                        <span className="prefix" aria-hidden>📝</span>
                        <input
                          type="text"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          placeholder={t('description_placeholder')}
                        />
                      </div>
                    </label>

                    {/* Date */}
                    <label className="wide">
                      <span>{t('date')}</span>
                      <div className="input-group">
                        <span className="prefix" aria-hidden>📅</span>
                        <input
                          type="datetime-local"
                          value={txDate}
                          onChange={(e) => setTxDate(e.target.value)}
                          required
                        />
                      </div>
                    </label>

                    <div className="actions">
                      <button
                        className="btn btn-primary"
                        type="submit"
                        disabled={submitting || (txType === 'withdraw' && lots.length === 0)}
                      >
                        {submitting ? t('saving') : t('add_transaction')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              {/* Transactions list */}
              <div className="card card-tight">
                <h3>{t('transaction_history')}</h3>
                <input
                  className="search-input"
                  type="text"
                  placeholder={t('search_placeholder')}
                  value={txSearchQuery}
                  onChange={(e) => setTxSearchQuery(e.target.value)}
                />
                <div className="transactions">
                  {txs.map((tr) => {
                    const amt = parseFloat(tr.amount_grams);
                    const signClass = amt >= 0 ? 'positive' : 'negative';
                    return (
                      <div className="tx-item" key={tr.id}>
                        <div className="tx-main">
                          <div className="currency-group">
                            <span className={'currency-chip ' + signClass}>
                              <span className="code">{formatPurity(tr.purity)}%</span>{' '}
                              <span className="val">{formatGrams(Math.abs(amt))} g</span>
                            </span>
                          </div>
                          <div className="user-actions-inline" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              className="btn btn-ghost btn-icon"
                              title={t('edit')}
                              aria-label={t('edit')}
                              onClick={() => openEditTx(tr)}
                            >
                              <FiEdit2 />
                            </button>
                            <button
                              type="button"
                              className="btn btn-ghost btn-icon"
                              title={t('delete')}
                              aria-label={t('delete')}
                              onClick={() => handleDeleteTx(tr.id)}
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                          <div className="tx-desc">{tr.description || '—'}</div>
                        </div>
                        <div className="tx-sub muted small">
                          {formatDate(tr.created_at)} • {tr.type} • {t('lot')} #{tr.lot_id} •{' '}
                          {t('balance')}: {formatGrams(tr.balance_after)} g
                        </div>
                      </div>
                    );
                  })}
                  {txs.length === 0 && <div className="empty muted">{t('no_transactions')}</div>}
                </div>
              </div>
            </>
          ) : (
            <div className="empty muted">{t('select_customer')}</div>
          )}
        </section>
      </main>

      {/* Client modal */}
      {showClientModal && (
        <div className="modal-backdrop" onClick={closeClientModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="clientModalTitle">
            <div className="modal-header">
              <h3 id="clientModalTitle">{clientModalMode === 'edit' ? t('edit_client') : t('new_client')}</h3>
            </div>
            <form className="modal-body" onSubmit={handleSaveClient}>
              <label>
                <span>{t('name')}</span>
                <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t('name')} required />
              </label>
              <label>
                <span>{t('additional_info')}</span>
                <input type="text" value={newInfo} onChange={(e) => setNewInfo(e.target.value)} placeholder={t('additional_info')} />
              </label>
              <label>
                <span>{t('phone')}</span>
                <input type="tel" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder={t('phone')} />
              </label>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={closeClientModal}>{t('cancel')}</button>
                <button type="submit" className="btn btn-primary">{clientModalMode === 'edit' ? t('save') : t('create')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit gold transaction modal */}
      {showEditTx && editingTx && (
        <div className="modal-backdrop" onClick={closeEditTx}>
          <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="editGoldTxTitle">
            <div className="modal-header">
              <h3 id="editGoldTxTitle">{t('edit_transaction')}</h3>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSaveEditTx} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div className="muted small">
                  {t('date')}: {formatDate(editingTx.created_at)}
                  {' • '}{editingTx.type}
                  {' • '}{t('purity')}: <strong>{formatPurity(editingTx.purity)}%</strong>
                  {' • '}{t('lot')} #{editingTx.lot_id}
                </div>

                <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <span>{t('amount')} (g)</span>
                  <input
                    type="number"
                    step="0.001"
                    min="0.001"
                    value={editTxAmount}
                    onChange={(e) => setEditTxAmount(e.target.value)}
                    required
                    style={{ width: '100%' }}
                  />
                </label>

                <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <span>{t('description')}</span>
                  <input
                    type="text"
                    value={editTxDescription}
                    onChange={(e) => setEditTxDescription(e.target.value)}
                    style={{ width: '100%' }}
                  />
                </label>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '4px' }}>
                  <button type="button" className="btn btn-ghost" onClick={closeEditTx} disabled={editTxSubmitting}>
                    {t('cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={editTxSubmitting}>
                    {editTxSubmitting ? t('saving') : t('save')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* All-clients gold totals modal */}
      {showTotals && (
        <div className="modal-backdrop" onClick={closeTotals}>
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="goldTotalsTitle"
          >
            <div className="modal-header">
              <h3 id="goldTotalsTitle">{t('all_clients_gold_total')}</h3>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: '18px', fontWeight: 700, marginBottom: '12px' }}>
                {t('total')}: {formatGrams(globalTotals.total_grams)} g
              </div>
              {globalTotals.by_purity && globalTotals.by_purity.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {globalTotals.by_purity.map((row) => (
                    <div
                      key={row.purity}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '8px 10px',
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        background: 'var(--panel-2)',
                      }}
                    >
                      <span>{formatPurity(row.purity)}%</span>
                      <strong>{formatGrams(row.grams)} g</strong>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty muted">{t('no_gold_lots')}</div>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" type="button" onClick={closeTotals}>
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
